import { Button, Grid, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AddAlertLineReportNote from "./AddAlertLineReportNote";
import AlertLineReportSingleNote from "./AlertLineReportSingleNote";
import { DocumentViewerContainer, PreviousNotesContainer } from "./styles";
import { AlertLineReportDTO } from "../../models/alert-line-report/alert-line-report-dto";
import { AlertLineReportNoteDTO } from "../../models/alert-line-report-note/alert-line-report-note-dto";
import { AlertLineReportNoteFilter } from "../../models/alert-line-report-note/alert-line-report-note-filter";
import { AlertLineReportNoteFileReviewFilter } from "../../models/alert-line-report-note-file-reviews/alert-line-report-note-file-review-filter";
import { Page } from "../../models/api/page";
import DocumentViewer from "../../reusable-components/DocumentViewer/DocumentViewer";
import { IFile } from "../../reusable-components/DocumentViewer/interfaces";
import { AlertLineReportNoteApiService } from "../../services/alert-line/alert-line-report-note-api.service";
import { AlertLineReportNoteFileDownloadApiService } from "../../services/alert-line-report-note-file-download/alert-line-report-note-file-download-api.service";
import { AlertLineReportNoteFileReviewApiService } from "../../services/alert-line-report-note-file-review/alert-line-report-note-file-review-api.service";
import DialogTitle from "../DialogTitle/DialogTitle";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
interface Props {
  alertLineReport: AlertLineReportDTO;
  asReviewer?: boolean;
  viewOnly?: boolean;
  forExport?: boolean;
  allNotes?: AlertLineReportNoteDTO[];
}

interface State {
  loading: boolean;
  error: string;
  page: Page;
  isFirstLoading: boolean;
  notes: AlertLineReportNoteDTO[];
  totalItems: number;
  fileToView: IFile | null;
  fileToViewNoteId: string;
}

const AlertLineReportNotes = ({ alertLineReport, asReviewer, viewOnly, forExport, allNotes }: Props) => {
  const tRP = "components.alert_line_report_notes";
  const { t } = useTranslation();
  const theme = useTheme();
  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    page: {
      pageNumber: 0,
      pageSize: 5,
    },
    isFirstLoading: true,
    notes: allNotes ?? [],
    totalItems: allNotes?.length ?? 0,
    fileToView: null,
    fileToViewNoteId: "",
  });

  //#region load notes
  const loadNotes = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const page = {
        pageNumber: forExport ? 0 : state.page.pageNumber,
        pageSize: forExport ? 100000 : state.isFirstLoading ? state.page.pageSize : state.page.pageSize + 5,
      };

      const apiService = new AlertLineReportNoteApiService();
      if (!asReviewer) {
        apiService.useLoadItemsPath("/GetPublicAlertLineReportNotes");
      }

      const response = await apiService.loadItems(
        {
          alertLineReportId: alertLineReport.id,
        } as AlertLineReportNoteFilter,
        "",
        undefined,
        page,
        undefined,
        60,
        undefined
      );
      if (!response.error) {
        setState((state) => ({
          ...state,
          notes: response.items,
          totalItems: response.totalItems,
          isFirstLoading: false,
          page,
        }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [alertLineReport.id, state.isFirstLoading, state.page.pageNumber, asReviewer, state.page.pageSize]);
  //#endregion

  //#region set file as viewed
  const handleSetFileAsViewed = useCallback(async () => {
    try {
      const response = await new AlertLineReportNoteFileReviewApiService().loadItems({
        alertLineReportNoteId: state.fileToViewNoteId,
      } as AlertLineReportNoteFileReviewFilter);
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    }
  }, [state.fileToViewNoteId]);
  //#endregion

  //#region successfull download
  const handleSuccessfullDownload = useCallback(async () => {
    try {
      const response = await new AlertLineReportNoteFileDownloadApiService().loadItems({
        alertLineReportNoteId: state.fileToViewNoteId,
      } as AlertLineReportNoteFileReviewFilter);
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    }
  }, [state.fileToViewNoteId]);
  //#endregion

  //#region set view file
  const setFileToView = useCallback((file: IFile, noteId: string) => {
    setState((state) => ({
      ...state,
      fileToView: file,
      fileToViewNoteId: noteId,
    }));
  }, []);
  //#endregion

  useEffect(() => {
    !forExport && loadNotes();
  }, [forExport]);

  return (
    <>
      {forExport ? (
        <></>
      ) : viewOnly ? (
        <Typography
          variant="body2"
          color={{ color: theme.palette.secondary.light }}
        >
          {`${t(`${tRP}.notes_not_allowed_in_view_only`)}`}
        </Typography>
      ) : (
        <AddAlertLineReportNote
          alertLineReport={alertLineReport}
          handleUpdateParent={loadNotes}
          asReviewer={asReviewer}
          latestSuggestedNote={
            state.notes.filter((_note) => !!_note.basedOn).flatMap((_note) => _note.basedOn)[0] ?? undefined
          }
        />
      )}
      <LoadingOverlay loading={state.loading}>
        <PreviousNotesContainer>
          {state.notes.map((note, idx) => (
            <AlertLineReportSingleNote
              key={idx}
              note={note}
              handleFileToView={setFileToView}
              alertLineReport={alertLineReport}
              forExport={forExport}
            />
          ))}
          {state.notes.length < state.totalItems && !forExport && (
            <Button
              onClick={loadNotes}
              style={{ width: "150px", textTransform: "none", alignSelf: "center" }}
            >
              {`${t(`${tRP}.load_more`)}`}
            </Button>
          )}
        </PreviousNotesContainer>
      </LoadingOverlay>

      {state.fileToView && (
        <DocumentViewerContainer open={!!state.fileToView}>
          <DialogTitle
            title=""
            handleClose={() => setState((state) => ({ ...state, fileToView: null }))}
          />
          <Grid>
            {state.fileToView && (
              <DocumentViewer
                file={state.fileToView}
                bypasAllowDownload
                handleSuccessfullView={handleSetFileAsViewed}
                handleSuccessfullDownload={handleSuccessfullDownload}
              />
            )}
          </Grid>
        </DocumentViewerContainer>
      )}
    </>
  );
};
export default AlertLineReportNotes;
